'use client'

import { useEffect, useState } from 'react'
import Script from 'next/script'
import { usePathname } from 'next/navigation'
import { GOOGLE_ADS_ID, googleAdsEnabled } from '@/lib/analytics/google-ads'
import {
  GA_MEASUREMENT_ID,
  gaDisableFlag,
  googleAnalyticsEnabled,
  setGoogleAnalyticsCollection,
} from '@/lib/analytics/google-analytics'
import { shouldLoadMarketingTag } from '@/lib/analytics/tag-scope'
import { flushSignUpConversionSoon, installBookingLinkListener } from '@/lib/analytics/browser'
import { isNativeApp } from '@/lib/native-app'
import { CONSENT_STORAGE_KEY } from '@/lib/consent/store'

/**
 * Loads Google's tags — on the public marketing surface only.
 *
 * ONE gtag.js load serves both the Ads conversion tag (`AW-…`) and the GA4
 * property (`G-…`). That is deliberate: the Consent Mode block below is the
 * lawfulness of the whole thing, and a second component with its own copy of it
 * is how the two drift apart. Google supports any number of `config` ids against
 * a single library load, so there is nothing to gain from a second script.
 *
 * Mounted once in the root layout rather than in each marketing layout, because
 * the Ads tag has to be present in TWO places for attribution to work at all: on
 * the landing page, where it converts the `?gclid=` on an ad click into the
 * `_gcl_aw` cookie, and on /email-entry/sign-up, where the conversion fires and
 * reads that cookie back. Mount it in only one and the chain silently breaks.
 *
 * The scope rule (lib/analytics/tag-scope.ts: marketing PATH, on the marketing
 * HOST, outside the native shell) is the point of the component; see
 * marketing-paths.ts for why the path half is an allowlist. Host and shell are
 * only knowable in the browser, so on the server render — and on the first
 * client render — the tag is simply off; it switches on in an effect. Note that the allowlist alone
 * is NOT sufficient for GA4 — a `<Script>` that unmounts does not unload, so the
 * runtime kill switch in lib/analytics/google-analytics.ts is what actually keeps
 * authenticated route URLs out of GA. Ads needs no equivalent: it reports
 * conversions, not pageviews, and fires only from the sign-up form.
 *
 * SPA navigation: GA4's Enhanced Measurement counts "page changes based on
 * browser history events" (on by default in the data stream), which is what
 * `next/link` navigations between marketing pages are. We deliberately do NOT
 * also send a manual `page_view` — with that setting on, doing both double-counts
 * every marketing pageview, and a doubled funnel is worse than a plain one.
 */
export function GoogleTags() {
  const pathname = usePathname()
  const [client, setClient] = useState<{ hostname: string; nativeApp: boolean } | null>(null)
  useEffect(() => {
    setClient({ hostname: window.location.hostname, nativeApp: isNativeApp() })
  }, [])
  const onMarketingSurface =
    client !== null &&
    shouldLoadMarketingTag({ pathname, hostname: client.hostname, nativeApp: client.nativeApp })

  // Sticky: once gtag.js is in the document it cannot be taken back out, so
  // keep rendering the tags after the visitor leaves the marketing surface
  // rather than pretending an unmount undid anything. What actually stops
  // collection off-surface is the effect below.
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    if (onMarketingSurface) setLoaded(true)
  }, [onMarketingSurface])

  // Follow the visitor across client-side navigations, in both directions:
  // leaving the marketing surface disables GA before Enhanced Measurement can
  // report an app URL, and coming back re-enables it.
  useEffect(() => {
    setGoogleAnalyticsCollection(onMarketingSurface)
  }, [onMarketingSurface])

  // Site-wide conversions that aren't owned by one button (lib/analytics/
  // browser.ts): booking-link clicks, and the one-shot `sign_up` that
  // /api/auth/complete-profile leaves in a cookie. Re-checked on every
  // marketing navigation so the first page after workspace creation fires it.
  useEffect(() => {
    if (!loaded) return
    return installBookingLinkListener()
  }, [loaded])
  useEffect(() => {
    if (!onMarketingSurface) return
    return flushSignUpConversionSoon()
  }, [onMarketingSurface, pathname])

  const adsOn = googleAdsEnabled()
  const gaOn = googleAnalyticsEnabled()
  if (!adsOn && !gaOn) return null
  if (!onMarketingSurface && !loaded) return null

  // Either id loads the same library; prefer GA4's so a deployment configured
  // with only GA4 still gets a loader.
  const loaderId = gaOn ? GA_MEASUREMENT_ID : GOOGLE_ADS_ID

  return (
    <>
      <Script
        id='google-gtag'
        strategy='afterInteractive'
        src={`https://www.googletagmanager.com/gtag/js?id=${loaderId}`}
      />
      <Script id='google-gtag-init' strategy='afterInteractive'>
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = window.gtag || gtag;

          // Consent Mode v2, denied by default for the UK and EEA.
          //
          // We advertise into the UK, where PECR requires consent BEFORE an
          // advertising cookie is set. Denying by region is what makes the tag
          // lawful to ship: in those regions gtag falls back to cookieless
          // pings, so nothing is stored and conversions simply go unattributed
          // until components/consent/CookieBanner.tsx calls
          // gtag('consent','update',…).
          //
          // Australia, our other market, has no equivalent prior-consent rule,
          // so it is granted and attribution works today. Getting this backwards
          // is not a cosmetic error: without a regional default, Google drops
          // UK/EEA conversions outright AND the cookie is set unlawfully.
          gtag('consent', 'default', {
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            analytics_storage: 'denied',
            region: [
              'GB','AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR',
              'HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI',
              'ES','SE','IS','LI','NO','CH',
            ],
          });
          gtag('consent', 'default', {
            ad_storage: 'granted',
            ad_user_data: 'granted',
            ad_personalization: 'granted',
            analytics_storage: 'granted',
          });

          ${
            gaOn
              ? `
          // A standing rejection has to be honoured BEFORE the config call
          // below, not corrected a moment later by lib/consent/store.ts: the
          // config sends GA's first page_view synchronously, so a returning
          // visitor who pressed Reject would otherwise have their landing hit
          // measured on every subsequent visit. Read synchronously from the same
          // key the consent store writes.
          try {
            if (window.localStorage.getItem('${CONSENT_STORAGE_KEY}') === 'denied') {
              window['${gaDisableFlag()}'] = true;
            }
          } catch (e) {}
          `
              : ''
          }

          gtag('js', new Date());
          ${adsOn ? `gtag('config', '${GOOGLE_ADS_ID}');` : ''}
          ${gaOn ? `gtag('config', '${GA_MEASUREMENT_ID}');` : ''}
        `}
      </Script>
    </>
  )
}
